// The music director: from what the player is doing (a menu, a race at its start or on its last lap, a workout block,
// the results) to what the music should be: which piece, how intense, how loud. Pure, with a little memory so the
// intensity never flaps: it moves at most one step at a time and holds each step a few seconds.
import { MAX_INTENSITY, type SongId } from './songs.js';

export type Scene =
  | { kind: 'silent' }
  | { kind: 'menu' }
  | { kind: 'results'; podium: boolean }
  | {
      kind: 'ride';
      /** What is ridden: a race with a finish (any competitive mode), a workout, or a free ride without a finish. */
      mode: 'race' | 'workout' | 'free';
      state: 'countdown' | 'running' | 'done';
      /** Share of the race ridden by the followed rider, 0-1 (laps or distance to the finish). */
      progress: number;
      lastLap: boolean;
      /** The followed rider has crossed the line (others may still be racing). */
      finished: boolean;
      paused: boolean;
      /** Effort as a share of FTP (workout target or power), for the pieces that follow the effort. */
      effort: number;
    };

/** What the player should do. */
export interface Cue {
  song: SongId | null;
  intensity: number;
  /** Trim of the music, 0-1: lower under a countdown, a pause, the end of a race. */
  level: number;
  /** Muffled (a pause menu over the race). */
  muffle: boolean;
}

export const SILENT: Cue = { song: null, intensity: 0, level: 0, muffle: false };

/** Seconds an intensity step holds before the next step up or down. */
export const HOLD_S = 6;

const unit = (x: number) => (Number.isFinite(x) ? Math.max(0, Math.min(1, x)) : 0);

/** The cue a scene asks for, without memory. */
export function cueOf(s: Scene): Cue {
  switch (s.kind) {
    case 'silent': return SILENT;
    case 'menu': return { song: 'dawn', intensity: 1, level: 1, muffle: false };
    case 'results': return { song: 'podium', intensity: s.podium ? 2 : 1, level: 1, muffle: false };
    case 'ride': {
      const song: SongId = s.mode === 'race' ? 'peloton' : 'tempo', p = unit(s.progress), e = unit(s.effort / 1.2);
      if (s.state === 'countdown') return { song, intensity: 0, level: 0.85, muffle: false };
      let intensity: number;
      if (s.mode === 'race') intensity = s.lastLap || p >= 0.7 ? 3 : p >= 0.3 ? 2 : 1;
      else if (s.mode === 'workout') intensity = e >= 0.75 ? 3 : e >= 0.5 ? 2 : 1;
      else intensity = e >= 0.6 ? 2 : 1;
      if (s.state === 'done') return { song, intensity: 1, level: 0.6, muffle: false };
      if (s.finished) return { song, intensity: Math.min(intensity, 2), level: 0.7, muffle: false };
      return { song, intensity, level: s.paused ? 0.35 : 1, muffle: s.paused };
    }
  }
}

/** Keeps the cue steady: the intensity changes one step at a time, and holds HOLD_S between steps while the piece plays on. */
export class Director {
  cue: Cue = SILENT;
  private since = -Infinity;
  private phase = '';

  /** The cue for `scene` at time `now` (s, any monotonic clock). */
  step(scene: Scene, now: number): Cue {
    const want = cueOf(scene), cur = this.cue, phase = phaseOf(scene);
    // another piece, or another moment of the ride (the gun, the line, a pause): the music answers at once
    if (want.song !== cur.song || want.song === null || phase !== this.phase) { this.cue = want; this.since = now; this.phase = phase; return want; }
    let intensity = cur.intensity;
    if (want.intensity !== cur.intensity && now - this.since >= HOLD_S) {
      intensity = cur.intensity + Math.sign(want.intensity - cur.intensity);
      this.since = now;
    }
    this.cue = { ...want, intensity: Math.max(0, Math.min(MAX_INTENSITY, intensity)) };
    return this.cue;
  }
}

/** The moments of a scene between which the intensity may jump instead of stepping. */
const phaseOf = (s: Scene): string => (s.kind === 'ride' ? `${s.kind}:${s.mode}:${s.state}:${s.finished}:${s.paused}` : s.kind);
